import React, { useState, useEffect, useContext } from "react";
import DynamicIcon from "../../components/dynamicicon";
import { useParams } from "react-router-dom";
import * as Constants from "../../constants";
import { ShareContext } from "../../context/ShareContext";
import BackLoader from "../../components/backloader";

// "2025-12-31" -> "OCTOBER - DECEMBER, 2025" (parsed from the text, so no timezone shift)
const getQuarterLabel = (dateStr) => {
  if (!dateStr) return "";
  const [y, m] = dateStr.split("-").map(Number);
  if (!y || !m) return dateStr;
  const q = Math.floor((m - 1) / 3);
  return [
    `JANUARY - MARCH, ${y}`,
    `APRIL - JUNE, ${y}`,
    `JULY - SEPTEMBER, ${y}`,
    `OCTOBER - DECEMBER, ${y}`,
  ][q];
};

const EXPORTS = {
  excel: { ext: "xlsx", label: "Excel" },
  msword: { ext: "docx", label: "Word" },
  pdf: { ext: "pdf", label: "PDF" },
};

// counts are numbers; anything missing is 0
const n = (v) => (typeof v === "number" && !isNaN(v) ? v : Number(v) || 0);

function Njc3Report() {
  const { eventtype, judgeid, divisionid, lastdate } = useParams();
  const { token, setIsExpired } = useContext(ShareContext);

  const [judgeName, setJudgeName] = useState("");
  const [division, setDivision] = useState("");
  const [courtNo, setCourtNo] = useState("");
  const [chiefJudgeName, setChiefJudgeName] = useState("");
  const [quarterCovered, setQuarterCovered] = useState(getQuarterLabel(lastdate));
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    onSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reportUrl = `${eventtype || "all"}/${divisionid}/${judgeid}/${lastdate}`;

  const onSearch = async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${Constants.apiUrl}/report/njc-3/${reportUrl}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await resp.json();
      setLoading(false);
      if (data.code == 0) {
        setCases(data.items || []);
        setDivision(data.division || "");
        setJudgeName(data.judgename || "");
        setCourtNo(data.courtNo || "");
        setChiefJudgeName(data.chiefJudgeName || "");
        if (data.quarterCovered) setQuarterCovered(data.quarterCovered);
      } else if (data.code == -10) {
        setIsExpired(true);
      } else {
        setErr(data.status || "Unable to load the report");
      }
    } catch (error) {
      setLoading(false);
      console.error(error);
      setErr(Constants.serverErr);
    }
  };

  const onExport = async (doctype) => {
    setErr("");
    setLoading(true);
    try {
      const resp = await fetch(
        `${Constants.apiUrl}/report/njc-3-export/${doctype}/${reportUrl}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (resp.status === 409) {
        setIsExpired(true);
        return;
      }
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `NJC-MCPJ-3C-${lastdate}.${EXPORTS[doctype].ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setErr("Unable to export the report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const total = (key) => cases.reduce((sum, item) => sum + n(item[key]), 0);

  const linkStyle = { cursor: "pointer" };

  return (
    <div>
      <div>
        <div className="no-print" style={{ marginBottom: 12 }}>
          <span className="specialaction" style={linkStyle} onClick={() => onExport("excel")}>
            Excel
          </span>{" "}
          |{" "}
          <span className="specialaction" style={linkStyle} onClick={() => onExport("msword")}>
            Word
          </span>{" "}
          |{" "}
          <span className="specialaction" style={linkStyle} onClick={() => onExport("pdf")}>
            PDF
          </span>{" "}
          |{" "}
          <span className="specialaction" style={linkStyle} onClick={() => window.print()}>
            Print
          </span>
          {err && <div className="error-message">{err}</div>}
        </div>

        <div style={{ textAlign: "center", fontWeight: "bold" }}>
          NATIONAL JUDICIAL COUNCIL
        </div>
        <div style={{ textAlign: "center" }}>
          COMMITTEE ON PERFORMANCE EVALUATION OF JUDICIAL OFFICERS OF SUPERIOR
          COURTS OF RECORD
        </div>

        <div style={{ marginBottom: 16 }}>
          <div>
            <strong>STATE: LAGOS</strong>
          </div>
          <div>
            <strong>COURT:</strong>{" "}
            <span>
              {courtNo ? `${courtNo} / ` : ""}
              {division}
            </span>
          </div>
        </div>

        <div>
          <div>DETAILS OF CONSIDERED JUDGEMENTS DURING THE QUARTER ENDED</div>
          <div>{quarterCovered}</div>
        </div>
        <div className="table">
          <table className="case-table">
            <thead>
              <tr>
                <th>Cases</th>
                <th>(1)<br />CASES BROUGHT FORWARD FROM LAST QUARTER</th>
                <th>(2)<br />CASES ASSIGNED THIS QUARTER</th>
                <th>(3)<br />TOTAL OF 1 AND 2</th>
                <th>(4)<br />NO. OF CONTESTED CASES AND JUDGEMENT GIVEN</th>
                <th>(5)<br />NON CONTESTED AND STRUCK OUT CASES</th>
                <th colSpan={2}>(6)<br />DECISIONS ON INTERLOCUTORY PROCEEDINGS</th>
                <th>(7)<br />TOTAL NO OF CASES DISPOSED OF DURING THE QUARTER</th>
                <th>(8)<br />CASES PENDING AT THE END OF THE QUARTER</th>
              </tr>
              <tr>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
                <th>Contested</th>
                <th>Non-Contested</th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {cases.map((opt, index) => (
                <tr key={index}>
                  <td>{opt.case_Type || "-"}</td>
                  <td>{n(opt.previousQuarterScheduled)}</td>
                  <td>{n(opt.currentQuarterUnconcluded)}</td>
                  <td>{n(opt.totalCount)}</td>
                  <td>{n(opt.casesWithMultipleClaimants)}</td>
                  <td>{n(opt.casesWithStruckout)}</td>
                  <td>{n(opt.interlocContested)}</td>
                  <td>{n(opt.interlocNotContested)}</td>
                  <td>{n(opt.casesTotalDispose)}</td>
                  <td>{n(opt.pendingCases)}</td>
                </tr>
              ))}

              <tr style={{ fontWeight: "bold" }}>
                <td>Total</td>
                <td>{total("previousQuarterScheduled")}</td>
                <td>{total("currentQuarterUnconcluded")}</td>
                <td>{total("totalCount")}</td>
                <td>{total("casesWithMultipleClaimants")}</td>
                <td>{total("casesWithStruckout")}</td>
                <td>{total("interlocContested")}</td>
                <td>{total("interlocNotContested")}</td>
                <td>{total("casesTotalDispose")}</td>
                <td>{total("pendingCases")}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 8 }}>
          <DynamicIcon iname="asterisk" />{" "}
          <span style={{ fontSize: 12 }}>
            * Total Number of Cases Disposed During the Quarter, Column 7 above
            = Column 4+5.
          </span>
        </div>
        <div style={{ marginTop: 8 }}>
          <DynamicIcon iname="asterisk" />{" "}
          <span style={{ fontSize: 12 }}>
            * Cases Pending at the end of the Quarter, Column 8 above = Column 3
            Minus Column 7.
          </span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 24,
          }}
        >
          <div>
            <strong>NAME OF JUDGE:</strong> <span>{Constants.judgeTitle(judgeName)}</span>
          </div>
          <div>
            <strong>CONFIRMED BY ME, CHIEF JUDGE:</strong>{" "}
            <span>{Constants.judgeTitle(chiefJudgeName, "____________________")}</span>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 32,
          }}
        >
          <div>
            <span>Signature: _______________________</span> <span>Date:</span>
            <span>__________________________</span>
          </div>
          <div>
            <span>Signature: _______________________</span> <span>Date:</span>
            <span>__________________________</span>
          </div>
        </div>
      </div>
      <BackLoader open={loading} />
    </div>
  );
}

export default Njc3Report;
